import { toast } from "react-toastify";
import { SET_RENEWAL_DATA } from "./types";
import { addMonths } from "date-fns";
/**
 * Creates KYC Renewal action creators that use the provided apiHandler function.
 * This allows the actions to work with different API implementations
 * (e.g., admin-portal vs dashboard).
 *
 * @param apiHandler - The API handler function with signature (method, url, data)
 * @param options - Configuration options for app-specific behavior
 * @returns Object containing all KYC Renewal action creators
 */
export function createKYCRenewalActions(apiHandler, options = {}) {
    const { enableCustomerRenewalsAnalysis = false, axiosRedshift, formatDate, initialEndDateMonths = 12, partnerDistributerEndpoint, buildPartnerDistributerRequest, transformPartnerDistributerResponse, } = options;
    const setKYCRenewalData = (name, value) => (dispatch) => {
        dispatch({
            type: SET_RENEWAL_DATA,
            payload: { [name]: value },
        });
    };
    const getPartnerDistributerList = (partnerId) => async (dispatch) => {
        try {
            let requestData;
            let endpoint;
            // Use custom builder if provided, otherwise use defaults
            if (buildPartnerDistributerRequest) {
                requestData = buildPartnerDistributerRequest(partnerId);
                endpoint = partnerDistributerEndpoint || "/tenant/parent_tenants";
            }
            else if (partnerDistributerEndpoint === "/sessions/get-distributer") {
                // Dashboard pattern
                requestData = { partnerId };
                endpoint = "/sessions/get-distributer";
            }
            else {
                // Admin-portal pattern (default)
                requestData = { partner_type: "normal" };
                endpoint = partnerDistributerEndpoint || "/tenant/parent_tenants";
            }
            const response = await apiHandler("post", endpoint, requestData);
            let distributerList = [];
            // Use custom transformer if provided
            if (transformPartnerDistributerResponse) {
                distributerList = transformPartnerDistributerResponse(response, partnerId);
            }
            else if (partnerDistributerEndpoint === "/sessions/get-distributer") {
                // Dashboard pattern - ALWAYS treat keys as strings to avoid strict
                // equality mismatches with multiSelectedDistributersKey.
                const resData = response?.data || [];
                distributerList = resData.map((item) => {
                    const typedItem = item;
                    const text = typedItem.text ||
                        typedItem.org_name ||
                        typedItem.name ||
                        String(typedItem.id || "");
                    const key = String(typedItem.key ?? typedItem.id ?? typedItem.partnerId ?? "");
                    return {
                        text,
                        key,
                        distributeName: typedItem.distributeName || "",
                    };
                });
            }
            else {
                // Admin-portal pattern (default)
                const responseData = response?.data || [];
                const initAll = [{ text: "SELECT ALL", key: 1, distributeName: "ALL" }];
                const mappedData = responseData.map((orgData) => {
                    const typed = orgData;
                    return {
                        text: typed.org_name || "",
                        key: typed.id || 0,
                        distributeName: typed.distributor_org?.org_name || "",
                    };
                });
                distributerList = initAll.concat(mappedData);
            }
            const payload = partnerDistributerEndpoint === "/sessions/get-distributer"
                ? // Dashboard: always use string keys (auth.profile.org_id can be number)
                    partnerId !== undefined
                        ? [String(partnerId)]
                        : []
                : // Admin-portal: preserve numeric sentinel (SELECT ALL => 1)
                    partnerId !== undefined
                        ? [partnerId]
                        : [1];
            dispatch({
                type: SET_RENEWAL_DATA,
                payload: {
                    distributerList,
                    multiSelectedDistributersKey: payload,
                },
            });
        }
        catch (err) {
            console.error("getPartnerDistributerList error:", err);
            const error = err;
            const errMessage = error?.response?.data?.message ||
                error?.message ||
                "Something went wrong, try again";
            toast.error(errMessage);
        }
    };
    const handleMultiDropdown = (name, option) => (dispatch, getState) => {
        const { kycRenewal } = getState();
        const currentList = (kycRenewal[name] ||
            []);
        const isSessionsEndpoint = partnerDistributerEndpoint === "/sessions/get-distributer";
        // For the dashboard (sessions endpoint), we normalize all keys to strings to avoid
        // strict equality mismatches (e.g. 123 !== "123") when using indexOf.
        let dropDownList = isSessionsEndpoint
            ? currentList.map((value) => String(value))
            : [...currentList];
        const normalizedKey = isSessionsEndpoint
            ? String(option.key)
            : option.key;
        if (option.selected) {
            // Admin-portal pattern: remove "SELECT ALL" (1) if selecting something else
            if (!isSessionsEndpoint) {
                const index = dropDownList.indexOf(1);
                if (index > -1) {
                    dropDownList.splice(index, 1);
                }
            }
            dropDownList.push(normalizedKey);
            // Admin-portal: if selecting "SELECT ALL", only keep that
            if (option.key === 1 && !isSessionsEndpoint) {
                dropDownList = [1];
            }
            dispatch({
                type: SET_RENEWAL_DATA,
                payload: { [name]: dropDownList },
            });
        }
        else {
            // Prevent empty selection (matches legacy dashboard behavior).
            // This is intentionally NOT tied to partnerDistributerEndpoint since multiple
            // consumers expect at least one partner to remain selected.
            const index = dropDownList.indexOf(normalizedKey);
            if (index > -1) {
                const nextList = [...dropDownList];
                nextList.splice(index, 1);
                if (nextList.length === 0) {
                    return;
                }
                dropDownList = nextList;
            }
            dispatch({
                type: SET_RENEWAL_DATA,
                payload: {
                    [name]: dropDownList,
                },
            });
        }
    };
    const resetFilters = (partnerId) => (dispatch) => {
        const now = new Date();
        dispatch({
            type: SET_RENEWAL_DATA,
            payload: {
                renewalStartDate: addMonths(now, -6),
                renewalEndDate: partnerDistributerEndpoint === "/sessions/get-distributer"
                    ? now // Dashboard: current date
                    : addMonths(now, initialEndDateMonths), // Admin-portal: +12 months
                multiSelectedDistributersKey: partnerDistributerEndpoint === "/sessions/get-distributer"
                    ? [String(partnerId)]
                    : [partnerId],
                resetBtn: true,
            },
        });
    };
    // Customer Renewals Analysis action (admin-portal only)
    const getCustomerRenewalsAnalysis = enableCustomerRenewalsAnalysis
        ? (partnerId, filters) => async (dispatch) => {
            if (!axiosRedshift || !formatDate) {
                console.error("getCustomerRenewalsAnalysis requires axiosRedshift and formatDate");
                return;
            }
            try {
                dispatch({
                    type: SET_RENEWAL_DATA,
                    payload: {
                        isCustomerRenewalsLoading: true,
                        customerRenewalsData: null,
                        customerRenewalsError: null,
                    },
                });
                // Build query parameters
                const params = new URLSearchParams({ partnerId });
                params.append("startDate", formatDate(filters.startDate, "YYYY-MM-DD"));
                params.append("endDate", formatDate(filters.endDate, "YYYY-MM-DD"));
                params.append("usePrimarySite", String(filters.usePrimarySite));
                const response = await axiosRedshift.get(`/renewal-dashboard?${params.toString()}`);
                dispatch({
                    type: SET_RENEWAL_DATA,
                    payload: {
                        isCustomerRenewalsLoading: false,
                        customerRenewalsData: (response?.data ||
                            {}),
                    },
                });
            }
            catch (err) {
                console.error("getCustomerRenewalsAnalysis", err);
                const error = err;
                dispatch({
                    type: SET_RENEWAL_DATA,
                    payload: {
                        isCustomerRenewalsLoading: false,
                        customerRenewalsData: {},
                        customerRenewalsError: error?.response?.data?.error || error?.message,
                    },
                });
            }
        }
        : undefined;
    const actions = {
        setKYCRenewalData,
        getPartnerDistributerList,
        handleMultiDropdown,
        resetFilters,
    };
    if (getCustomerRenewalsAnalysis) {
        actions.getCustomerRenewalsAnalysis = getCustomerRenewalsAnalysis;
    }
    return actions;
}
